<p-panel header="Yarns" *ngIf="yarnInfo" [formGroup]="yarnInfo">
  @if (showTitle) {
    <div class="page__title">
      <div class="page__title_container">
        <h2>{{ 'Yarn_Info_Title' | translateWF: 'Yarns' }}</h2>
        <div class="page__title_buttons">
          <p-button
            [label]="'Yarn_Info_Add_Additional' | translateWF: 'Add Additional Yarn'"
            icon="pi pi-plus"
            variant="outlined"
            (click)="addYarn()"
            [disabled]="!allowToAddNewYarn"
            *ngIf="!readonly && canEdit"
          />
        </div>
      </div>
    </div>
  }

  <div class="yarn-list">
    <div *ngFor="let yarnEntry of yarnEntries.controls; let i = index" class="primary__card" [formGroup]="yarnEntry">
      <div class="grid-12 mb-[1rem] items-center">
        <div class="span-10">
          <div class="form-box-row">
            <label>{{ 'Yarn_Title' | translateWF: 'Yarn' }}</label>
            <p-autocomplete
              fluid
              formControlName="selectedYarn"
              id="yarn-autocomplete"
              #yarnSearch="cslServerSearch"
              [cslServerSearch]="fetchYarns(i)"
              [suggestions]="yarnSearch.results()"
              [forceSelection]="true"
              field="longDescription"
              [dropdown]="true"
              [readonly]="readonly"
            >
              <ng-template let-yarn pTemplate="item">
                <div class="p-list-item">
                  <div class="row-between-stretch gap-2">
                    <span>
                      <b>{{ yarn.code }}</b>
                    </span>
                    <span>
                      {{ yarn.longDescription }}
                    </span>
                  </div>
                </div>
              </ng-template>
            </p-autocomplete>
          </div>
        </div>
        <div class="span-2 row-end-stretch">
          <p-button icon="pi pi-trash" (click)="removeYarn(i)" *ngIf="!readonly && canEdit" />
        </div>
      </div>

      <!-- Yarn summary: code, name, status -->
      <div class="row-start-center gap-[1rem] mb-[1rem]" *ngIf="yarnEntry.get('selectedYarn')?.value?.id">
        <a class="font-bold text-primary cursor-pointer hover:underline" (click)="navigateToYarn(yarnEntry.get('selectedYarn')?.value)">
          {{ yarnEntry.get('selectedYarn')?.value?.code }}
        </a>
        <span class="text-color-secondary">
          {{ yarnEntry.get('selectedYarn')?.value?.longDescription }}
        </span>
        <p-tag
          [value]="getStatusLabel(yarnEntry.get('selectedYarn')?.value?.status) | pascalToNormalCase"
          [severity]="getStatusSeverity(yarnEntry.get('selectedYarn')?.value?.status)"
        />
      </div>

      <div class="grid-12 gap-[1rem]" *ngIf="yarnEntry.get('selectedYarn')?.value">
        <div class="col span-6">
          <div class="grid-auto-2">
            <div class="form-box">
              <label [for]="'yarnPosition' + i">{{ 'Yarn_Info_Position' | translateWF: 'Yarn Position' }}</label>
              <p-select
                id="yarnPosition"
                class="w-full"
                formControlName="yarnPosition"
                [options]="yarnPositionOptions"
                [readonly]="readonly"
                [ngClass]="{
                  'ng-invalid ng-dirty': parentFormSubmitted && yarnEntry.get('yarnPosition')?.invalid,
                }"
              />
            </div>
            <div class="form-box">
              <label [for]="'count' + i">{{ 'Yarn_Info_Count' | translateWF: 'Count' }}</label>
              <input type="text" [value]="yarnEntry.get('count')?.value" pInputText disabled class="cs-fill-width" />
            </div>
            <div class="form-box">
              <label [for]="'cones' + i">{{ 'Yarn_Info_Cones' | translateWF: 'Cones' }}</label>
              @if (!readonly) {
                <p-inputNumber
                  [id]="'cones' + i"
                  formControlName="cones"
                  [ngClass]="{
                    'ng-invalid ng-dirty': parentFormSubmitted && yarnEntry.get('cones')?.invalid,
                  }"
                />
              } @else {
                <input type="text" [value]="yarnEntry.get('cones')?.value" pInputText disabled class="cs-fill-width" />
              }
            </div>
            <div class="form-box">
              <label [for]="'usePercentage' + i">{{ 'Yarn_Info_Percentage' | translateWF: 'Percentage' }}</label>
              <p-inputGroup>
                @if (!readonly) {
                  <p-inputNumber
                    formControlName="usePercentage"
                    [readonly]="readonly"
                    [minFractionDigits]="2"
                    [maxFractionDigits]="2"
                    [ngClass]="{
                      'ng-invalid ng-dirty': parentFormSubmitted && yarnEntry.get('usePercentage')?.invalid,
                    }"
                  ></p-inputNumber>
                } @else {
                  <input type="text" [value]="yarnEntry.get('usePercentage')?.value" pInputText disabled class="cs-fill-width" />
                }

                <p-inputgroup-addon>%</p-inputgroup-addon>
              </p-inputGroup>
            </div>
            <div class="form-box">
              <label [for]="'loopLength' + i">{{ 'Yarn_Info_Loop_Length' | translateWF: 'Loop Length' }}</label>
              @if (!readonly) {
                <p-inputNumber
                  [id]="'loopLength' + i"
                  formControlName="loopLength"
                  [minFractionDigits]="2"
                  [readonly]="readonly"
                  [disabled]="readonly"
                  [ngClass]="{
                    'ng-invalid ng-dirty': parentFormSubmitted && yarnEntry.get('loopLength')?.invalid,
                  }"
                />
              } @else {
                <input type="text" [value]="yarnEntry.get('loopLength')?.value" pInputText disabled class="cs-fill-width" />
              }
            </div>
            <div class="form-box">
              <label [for]="'loopsPerNeedle' + i">{{ 'Yarn_Info_Loops_Per_Needle' | translateWF: 'Loops Per Needle' }}</label>
              @if (!readonly) {
                <p-inputNumber
                  formControlName="loopsPerNeedle"
                  [id]="'loopsPerNeedle' + i"
                  [readonly]="readonly"
                  [disabled]="readonly"
                  [minFractionDigits]="2"
                  [ngClass]="{
                    'ng-invalid ng-dirty': parentFormSubmitted && yarnEntry.get('loopsPerNeedle')?.invalid,
                  }"
                />
              } @else {
                <input type="text" [value]="yarnEntry.get('loopsPerNeedle')?.value" pInputText disabled class="cs-fill-width" />
              }
            </div>
            <div class="form-box">
              <label [for]="'mtsRev' + i">{{ 'Yarn_Info_Meters_Rev' | translateWF: 'Meters/Rev' }}</label>
              <input type="text" [value]="yarnEntry.get('mtsRev')?.value | number: '1.2-4'" pInputText disabled class="cs-fill-width" />
            </div>
            <div class="form-box">
              <label [for]="'kgRev' + i">{{ 'Yarn_Info_Kg_Rev' | translateWF: 'Kg/Rev' }}</label>
              <input type="text" [value]="yarnEntry.get('kgRev')?.value | number: '1.2-4'" pInputText disabled class="cs-fill-width" />
            </div>
          </div>
          <div class="form-box">
            <label [for]="'lbsRev' + i">{{ 'Yarn_Info_Lbs_Rev' | translateWF: 'Lbs/Rev' }}</label>
            <input class="w-full" type="text" [value]="yarnEntry.get('lbsRev')?.value | number: '1.2-6'" pInputText disabled class="cs-fill-width" />
          </div>
        </div>

        <div class="span-6">
          <div class="yarn-composition" *ngIf="yarnEntry.get('selectedYarn')?.value?.yarnComposition?.length">
            <div class="composition-item" *ngFor="let comp of yarnEntry.get('selectedYarn')?.value?.yarnComposition">
              <label>
                {{ getCompositionLabel(comp.compositionCode) }}
              </label>
              <div class="composition-bar-wrapper">
                <div class="composition-bar">
                  <div class="fill" [style.width.%]="comp.percentage" [style.backgroundColor]="getCompositionColor(comp.compositionCode)"></div>
                </div>
                <div class="composition-percentage">{{ comp.percentage }}%</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</p-panel>
